import React from 'react'
import { Box, Text, BoxProps, createPolymorphicComponent } from '@mantine/core'
import { NavContext } from 'src/components/navigation/NavContext'
import { NavLink } from 'react-router-dom'
import { MobileNavIcon } from 'src/components/ui/Misc'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { DefaultTheme } from 'src/theme/theme'

type PageLayoutProps = {
    header?: React.ReactElement
    showFooterNav?: boolean
    children?: React.ReactNode
}

type LayoutWrapperProps = {
    $showFooter: boolean
}

const _LayoutWrapper = styled(Box) <LayoutWrapperProps>`
    height: 100%;
    display: grid;
    @media (max-width: ${DefaultTheme.breakpoints.md}) {
        grid-template-rows: ${props => props.$showFooter ? "auto 1fr auto" : "auto 1fr"};
    }
    @media (min-width: ${DefaultTheme.breakpoints.md}) {
        grid-template-rows: auto 1fr;
    }
`

const LayoutWrapper = createPolymorphicComponent<'div', BoxProps & LayoutWrapperProps>(_LayoutWrapper)

const _LayoutContainer = styled(Box)`
    padding: ${DefaultTheme.spacing.md};
    background-color: var(--mantine-color-gray-2);
    height: 100%;
    @media (max-width: ${DefaultTheme.breakpoints.md}) {
        max-width: 100vw;
    }    
`
const LayoutContainer = createPolymorphicComponent<'div', BoxProps>(_LayoutContainer)

type FooterNavItemProps = {
    $columns: number
}

const _FooterNavWrapper = styled(Box) <FooterNavItemProps>`
    height: 70px;
    display: grid;
    grid-template-columns: repeat(${props => props.$columns}, 1fr);
    & .mobile-nav-item {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-transform: uppercase;
        color: var(---mantine-color-gray-0);
        text-decoration: none;        
        & .mantine-Text-root {
            font-weight: 800;
        }
        &.active {
            background-color: var(--mantine-color-dark-6);
        }
        &:hover {
            background-color: var(--mantine-color-dark-6);
        }
    }
`
const FooterNavWrapper = createPolymorphicComponent<'div', BoxProps & FooterNavItemProps>(_FooterNavWrapper)

export const PageLayoutCalendar: React.FC<PageLayoutProps> = ({ header = null, children, showFooterNav = false }) => {

    const { mobile } = React.useContext(NavContext)

    return (

        <LayoutWrapper $showFooter={showFooterNav}>

            <motion.div
                variants={{
                    enter: { y: 0, opacity: 0, transition: { duration: 0.25 } },
                    target: { y: 0, opacity: 1, transition: { duration: 0.25 } },
                    exit: { y: 0, opacity: 0, transition: { duration: 0.25 } }
                }}
                initial="enter"
                animate="target"
                exit="exit"
                style={{ height: '100%', position: 'relative' }}
            >

                <Box px="md">
                    {header}
                </Box>

            </motion.div>

            <Box
                style={{
                    position: 'relative',
                    height: '100%',
                    overflow: 'hidden'
                }}
            >

                <motion.div
                    variants={{
                        enter: { y: -100, opacity: 0, transition: { duration: 0.25 } },
                        target: { y: 0, opacity: 1, transition: { duration: 0.25 } },
                        exit: { y: 0, opacity: 0, transition: { duration: 0.25 } }
                    }}
                    initial="enter"
                    animate="target"
                    exit="exit"
                    style={{ height: '100%', position: 'relative' }}
                >

                    <LayoutContainer>
                        {children}
                    </LayoutContainer>

                </motion.div>

            </Box>

            {showFooterNav && (
                <Box
                    style={theme => ({
                        backgroundColor: theme.colors.dark[9]
                    })}
                    hiddenFrom='md'
                >
                    <FooterNavWrapper $columns={mobile.length}>
                        {mobile.map((i, k) => {
                            return (
                                <NavLink
                                    key={`mobile-nav-item-${k}`}
                                    to={i.to}
                                    className="mobile-nav-item"
                                >
                                    <MobileNavIcon icon={i.icon} />
                                    <Text span size="xs" mt={2} c="white">{i.label}</Text>
                                </NavLink>
                            )
                        })}
                    </FooterNavWrapper>
                </Box>
            )}

        </LayoutWrapper>

    )
}